'use client';

import { useMemo, useState } from 'react';

import { SmallLotPricing } from '@ravago/shared/page-data/models/elements';

import { DEFAULT_PRODUCER_COLOR_CODE } from '../constants';
import { getSmallLotPricingForVariant, SmallLotUrlSelection, UniqueSmallLotColor } from '../utils/order-now-utils';

export type { SmallLotUrlSelection };

const getVariantsForColor = (smallLotPrices: SmallLotPricing[], colorId: number): string[] => [
   ...new Set(smallLotPrices.filter((entry) => entry.color.id === colorId).map((entry) => entry.color.producerColorCode || DEFAULT_PRODUCER_COLOR_CODE))
];

const deriveInitialSelection = (
   smallLotPrices: SmallLotPricing[],
   uniqueColors: UniqueSmallLotColor[],
   autoPreselect: boolean,
   urlSelection?: SmallLotUrlSelection
): { colorId: number | undefined; colorCode: string | undefined } => {
   if (urlSelection) {
      const { colorId, colorCode } = urlSelection;
      if (colorCode) return { colorId, colorCode };

      const variants = getVariantsForColor(smallLotPrices, colorId);
      return { colorId, colorCode: variants.length === 1 ? variants[0] : undefined };
   }

   if (autoPreselect && uniqueColors.length === 1) {
      const [{ id }] = uniqueColors;
      const variants = getVariantsForColor(smallLotPrices, id);
      return { colorId: id, colorCode: variants.length === 1 ? variants[0] : undefined };
   }

   return { colorId: undefined, colorCode: undefined };
};

interface Props {
   smallLotPrices: SmallLotPricing[];
   autoPreselect?: boolean;
   urlSelection?: SmallLotUrlSelection;
}

export const useSmallLotPricingState = ({ smallLotPrices, autoPreselect = true, urlSelection }: Props) => {
   const uniqueSmallLotColors = useMemo((): UniqueSmallLotColor[] => {
      const seen = new Set<number>();
      return smallLotPrices.reduce<UniqueSmallLotColor[]>((acc, entry) => {
         if (!seen.has(entry.color.id)) {
            seen.add(entry.color.id);
            acc.push({ id: entry.color.id, name: entry.color.name });
         }
         return acc;
      }, []);
   }, [smallLotPrices]);

   const [{ colorId: initialColorId, colorCode: initialColorCode }] = useState(() =>
      deriveInitialSelection(smallLotPrices, uniqueSmallLotColors, autoPreselect && !urlSelection, urlSelection)
   );

   const [selectedColorId, setSelectedColorId] = useState<number | undefined>(initialColorId);
   const [selectedProducerColorCode, setSelectedProducerColorCode] = useState<string | undefined>(initialColorCode);

   const variantsForSelectedColor = useMemo(
      () => (selectedColorId !== undefined ? getVariantsForColor(smallLotPrices, selectedColorId) : []),
      [selectedColorId, smallLotPrices]
   );

   const handleColorChange = (colorId: number) => {
      setSelectedColorId(colorId);
      setSelectedProducerColorCode(undefined);
   };

   const handleProducerColorCodeChange = (code: string) => {
      setSelectedProducerColorCode(code);
   };

   const clearSelection = () => {
      setSelectedColorId(undefined);
      setSelectedProducerColorCode(undefined);
   };

   const selectedPricing = useMemo(
      () =>
         selectedColorId !== undefined && selectedProducerColorCode !== undefined
            ? getSmallLotPricingForVariant(smallLotPrices, selectedColorId, selectedProducerColorCode)
            : null,
      [selectedColorId, selectedProducerColorCode, smallLotPrices]
   );

   return {
      uniqueSmallLotColors,
      variantsForSelectedColor,
      selectedColorId,
      selectedProducerColorCode,
      selectedPricing,
      handleColorChange,
      handleProducerColorCodeChange,
      clearSelection
   };
};
